import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faPencil,
  faGripDotsVertical,
} from "@fortawesome/pro-solid-svg-icons";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { humanizeValueType, type Question } from "./types";

export interface QuestionsTableProps {
  questions: Question[];
  onEdit: (q: Question) => void;
  selectedIds: string[];
  setSelectedIds: (ids: string[]) => void;
  draggable?: boolean;
}

const QuestionCheckbox = ({
  q,
  selectedIds,
  setSelectedIds,
}: {
  q: Question;
  selectedIds: string[];
  setSelectedIds: (ids: string[]) => void;
}): JSX.Element => {
  const checked = selectedIds.includes(q.id);
  return (
    <input
      type="checkbox"
      style={{ accentColor: "#418172" }}
      aria-label={`Select question ${q.name}`}
      checked={checked}
      onChange={() =>
        setSelectedIds(
          checked
            ? selectedIds.filter((id) => id !== q.id)
            : [...selectedIds, q.id]
        )
      }
    />
  );
};

// The Question prompt rendered as the standard green/underlined edit link with a
// pencil; clicking it opens the edit slideout (same as the old edit icon).
const QuestionContentCells = ({
  q,
  onEdit,
}: {
  q: Question;
  onEdit: (q: Question) => void;
}): JSX.Element => (
  <>
    <td>
      <button
        type="button"
        className="adminListEditLink q-edit-link"
        onClick={() => onEdit(q)}
      >
        {q.question}
        <FontAwesomeIcon icon={faPencil} className="adminListEditLink__icon" />
      </button>
    </td>
    <td>{q.name}</td>
    <td>{humanizeValueType(q.valueType)}</td>
    <td>{q.required ? <FontAwesomeIcon icon={faCheck} /> : ""}</td>
  </>
);

const SortableQuestionRow = ({
  q,
  onEdit,
  selectedIds,
  setSelectedIds,
}: {
  q: Question;
  onEdit: (q: Question) => void;
  selectedIds: string[];
  setSelectedIds: (ids: string[]) => void;
}): JSX.Element => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: `question:${q.id}` });
  return (
    <tr
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <td className="q-grip" {...attributes} {...listeners}>
        <FontAwesomeIcon icon={faGripDotsVertical} />
      </td>
      <td>
        <QuestionCheckbox
          q={q}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
        />
      </td>
      <QuestionContentCells q={q} onEdit={onEdit} />
    </tr>
  );
};

export const QuestionsTable = ({
  questions,
  onEdit,
  selectedIds,
  setSelectedIds,
  draggable = false,
}: QuestionsTableProps): JSX.Element => {
  if (questions.length === 0) {
    return <div className="text-muted p-2">No questions yet.</div>;
  }

  const header = (
    <thead>
      <tr>
        {draggable && <th className="q-grip" />}
        <th className="select-col" />
        <th style={{ width: "40%" }}>Question</th>
        <th style={{ width: "30%" }}>Name</th>
        <th style={{ width: "15%" }}>Type</th>
        <th style={{ width: "15%" }}>Required</th>
      </tr>
    </thead>
  );

  if (draggable) {
    return (
      <table className="questions-table">
        {header}
        <SortableContext
          items={questions.map((q) => `question:${q.id}`)}
          strategy={verticalListSortingStrategy}
        >
          <tbody>
            {questions.map((q) => (
              <SortableQuestionRow
                key={q.id}
                q={q}
                onEdit={onEdit}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
              />
            ))}
          </tbody>
        </SortableContext>
      </table>
    );
  }

  return (
    <table className="questions-table">
      {header}
      <tbody>
        {questions.map((q) => (
          <tr key={q.id}>
            <td>
              <QuestionCheckbox
                q={q}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
              />
            </td>
            <QuestionContentCells q={q} onEdit={onEdit} />
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default QuestionsTable;
